import React, { FC, useCallback, useEffect, useState } from 'react';
import { Skeleton } from '@material-ui/lab';
import Employee, { LegacyEmployee } from 'src/interfaces/api/Employee';
import useEmployeeAccess from 'src/hooks/api/useEmployeeAccess';
import { FormControl, FormHelperText, InputLabel, Select } from '@material-ui/core';
import useAuth from 'src/hooks/api/useAuth';
import Agency from 'src/interfaces/api/Agency';
import { CollectionResponse } from '../../../../interfaces/api/api';
import api from '../../../../config/api';
import { getJsonResponse } from '../../../../utils/ApiUtils';

const searchAgencies = {
  '1': [1, 3],
  '2': [2],
  '3': [1, 3],
};

interface Props {
  className?: any;
  value?: string;
  name?: string;
  size?: 'medium' | 'small';
  label?: string;
  legacyAgencyId?: number;
  onChange: (event?: any) => void;
}

const EmployeeSelect: FC<Props> = React.forwardRef<any, Props>(
  ({ onChange, legacyAgencyId, value, label, name, size = 'medium' }, ref) => {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const { fetch } = useAuth();

    const { getAgencyEmployees } = useEmployeeAccess();
    const { employees: legacyEmployees, loading: legacyLoading } = useLegacyEmployee();

    const getEmployee = useCallback((employeeId: string) => employees.find((employee) => employee.id === employeeId), [
      employees,
    ]);

    const getEmployeeLabel = useCallback(
      (employeeId: string) => {
        const employee = getEmployee(employeeId);
        return `${employee?.firstname} ${employee?.lastname}`;
      },
      [employees]
    );

    const removeDisabledEmployees = useCallback(() => {
      const employeesWithLegacyUser = employees.filter(
        (employee): employee is Employee & { legacyUserId: number } => !!employee.legacyUserId
      );
      return employeesWithLegacyUser.filter((employee) => getLegacyEmployeeId(employee.legacyUserId));
    }, [employees]);

    const getLegacyEmployeeId = useCallback(
      (legacyUserId: number) => {
        const employee = legacyEmployees.find((legacyEmployee) => legacyEmployee.user?.id === legacyUserId);
        return employee?.id;
      },
      [legacyEmployees]
    );

    const getEmployees = useCallback(
      async (agencies: Agency[]) => {
        let employees: Record<string, Employee> = {};

        for (const agency of agencies) {
          const collection = await getAgencyEmployees(agency.id);
          employees = Object.assign(
            {},
            employees,
            ...collection.items.map((employee) => ({
              [employee.id]: {
                ...employee,
              },
            }))
          );
        }

        return employees;
      },
      [legacyAgencyId]
    );

    useEffect(() => {
      if (legacyAgencyId) {
        const { endpoint, ...options } = api.agency.getAllAgency();

        fetch(endpoint, options)
          .then((response) => getJsonResponse<CollectionResponse<Agency>>(response))
          .then((collection) => {
            const agencies = collection.items.filter((agency) =>
              searchAgencies[`${legacyAgencyId}`].includes(agency.legacyId)
            );

            if (agencies.length > 0) {
              setLoading(true);
              getEmployees(agencies)
                .then((items) => {
                  setEmployees(Object.values(items));
                  setLoading(false);
                })
                .catch((e) => {
                  setError(e.message);
                  setLoading(false);
                });
            }
          });
      }
    }, [legacyAgencyId]);

    const handleSortEmployee = (employeeA: Employee, employeeB: Employee) => {
      return `${employeeA.firstname} ${employeeA.lastname}`.localeCompare(
        `${employeeB.firstname} ${employeeB.lastname}`
      );
    };

    if (loading || legacyLoading) {
      return <Skeleton height={50} />;
    }

    return (
      <FormControl size={size} variant='outlined' fullWidth error={undefined !== error}>
        {label && <InputLabel>{label}</InputLabel>}
        <Select name={name} label={label} value={value || ''} onChange={onChange} native>
          <option />
          {!loading ? (
            removeDisabledEmployees()
              .sort(handleSortEmployee)
              .map((employee) => (
                <option key={employee.id} value={getLegacyEmployeeId(employee.legacyUserId)}>
                  {getEmployeeLabel(employee.id)}
                </option>
              ))
          ) : (
            <span>Chargement...</span>
          )}
        </Select>
        {undefined !== error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }
);

const useLegacyEmployee = () => {
  const [employees, setEmployees] = useState<LegacyEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const { fetch } = useAuth();

  useEffect(() => {
    setLoading(true);
    const queryURL = `/api/employee/{ employees(all: true){ id, firstname, lastname, user{id, username} } }`;

    fetch(queryURL, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    })
      .then((resp) => {
        setLoading(false);
        return resp.json();
      })
      .then((data) => {
        if (data['data']['employees']) {
          setEmployees(data['data']['employees']);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  return { employees, loading };
};

export default React.memo(EmployeeSelect);
